import { useEffect, useState, useRef, useCallback } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import type { ParentInvitePublic } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button, buttonVariants } from "@/components/ui/button";
import { useAuth } from "@/lib/auth-context";
import { SchoolFilter, useSchoolFilterIds } from "@/components/school-filter";
import {
  DataTable,
  type DataTableColumn, type DataTableFetchParams,
} from "@/components/data-table";
import { Upload, Trash2, Check, Clock, Send } from "lucide-react";
import { PreviewPane, PreviewField, PreviewPaneToggle } from "@/components/preview-pane";
import { useGridPreferences } from "@/lib/use-grid-preferences";

type SortColumn = "last_name" | "email" | "created_at" | "claimed_at";

const PAGE_SIZE = 20;

export default function ParentInvitesPage() {
  const { user: authUser } = useAuth();
  const schoolIds = useSchoolFilterIds();
  const canManage = authUser?.roles.some((r) =>
    r.role_type === "super_admin" || r.role_type === "school_admin" || r.role_type === "org_admin",
  ) ?? false;
  const [searchParams, setSearchParams] = useSearchParams();
  useGridPreferences("parent-invites");

  const statusFilter = (searchParams.get("status") ?? "all") as "claimed" | "unclaimed" | "all";

  const [selected, setSelected] = useState<ParentInvitePublic | null>(null);
  const [detailInvite, setDetailInvite] = useState<ParentInvitePublic | null>(null);
  const [matchedPlayers, setMatchedPlayers] = useState<Array<{ id: string; first_name: string | null; surname: string | null; active: boolean }>>([]);
  const [pinned, setPinned] = useState(false);
  const [issuing, setIssuing] = useState(false); // 164 -- sending registration links
  const [reconciling, setReconciling] = useState(false); // 165 -- reconciliation
  // Bumped to force a DataTable refetch (navigation return, revoke, etc.).
  const [refreshKey, setRefreshKey] = useState(0);
  const location = useLocation();

  // Track navigation refreshes (not search param changes)
  const prevPathname = useRef(location.pathname);
  useEffect(() => {
    if (location.pathname === prevPathname.current && prevPathname.current !== "") return;
    prevPathname.current = location.pathname;
    setRefreshKey((k) => k + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on location.key; refetch only when the route actually changes
  }, [location.key]);

  const effectiveSchoolId = schoolIds[0] ?? "";

  function updateParams(updates: Record<string, string | null>) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [key, value] of Object.entries(updates)) {
        if (value === null || value === "") next.delete(key);
        else next.set(key, value);
      }
      return next;
    }, { replace: true });
  }

  // Server-side pagination/search/filter/sort, driven by <DataTable>. The
  // endpoint's param names differ from the DataTable contract, so map here.
  const fetcher = useCallback(
    async (params: DataTableFetchParams): Promise<{ data: ParentInvitePublic[]; total: number }> => {
      if (!effectiveSchoolId) return { data: [], total: 0 };
      const res = await api.getParentInvites(effectiveSchoolId, {
        q: params.search || undefined,
        status: statusFilter !== "all" ? statusFilter : undefined,
        sort_by: (params.sort ?? "created_at") as SortColumn,
        sort_dir: params.order,
        page: params.page,
        limit: params.per_page,
      });
      return { data: res.parent_invites, total: res.total };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed via fetchKey
    [effectiveSchoolId, statusFilter, refreshKey],
  );

  // Fetch detail on selection
  useEffect(() => {
    if (!selected || !effectiveSchoolId) {
      setDetailInvite(null);
      setMatchedPlayers([]);
      return;
    }
    setDetailInvite(selected);
    api.getParentInvite(effectiveSchoolId, selected.id)
      .then((res) => {
        setDetailInvite(res.parent_invite);
        setMatchedPlayers(res.matched_players);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on selected.id; the full object would refetch on every list refresh
  }, [selected?.id, effectiveSchoolId]);

  async function handleRevoke(inviteId: string) {
    if (!confirm("Are you sure you want to revoke this invite?")) return;
    try {
      await api.deleteParentInvite(effectiveSchoolId, inviteId);
      if (selected?.id === inviteId) { setSelected(null); setDetailInvite(null); }
      setRefreshKey((k) => k + 1); // refetch
    } catch (e) {
      window.alert(e instanceof api.ApiError ? e.message : "Failed to revoke invite");
    }
  }

  const columns: DataTableColumn<ParentInvitePublic>[] = [
    {
      key: "last_name", header: "Name", sortable: true, className: "font-medium",
      render: (inv) => `${inv.first_name} ${inv.last_name}`,
    },
    { key: "email", header: "Email", sortable: true, render: (inv) => inv.email },
    {
      key: "family_code", header: "Family Code",
      render: (inv) => <code className="text-xs bg-muted px-1 py-0.5 rounded">{inv.family_code}</code>,
    },
    { key: "phone", header: "Phone", render: (inv) => inv.phone ?? "--" },
    { key: "players", header: "Players", render: (inv) => inv.matched_player_count },
    {
      key: "created_at", header: "Imported", sortable: true,
      render: (inv) => new Date(inv.created_at).toLocaleDateString(),
    },
    {
      key: "status", header: "Status",
      render: (inv) =>
        inv.claimed_at ? (
          <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
            <Check className="h-3 w-3" /> Claimed
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">
            <Clock className="h-3 w-3" /> Pending
          </span>
        ),
    },
  ];

  if (canManage) {
    columns.push({
      key: "actions", header: "", headerClassName: "w-12",
      render: (inv) => (
        <div onClick={(e) => e.stopPropagation()}>
          {inv._actions.includes("revoke") && (
            <button
              className="text-destructive hover:text-destructive/80"
              onClick={() => handleRevoke(inv.id)}
              title="Revoke invite"
              data-action="revoke"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      ),
    });
  }

  const statusFilterControls = (
    <div className="flex gap-1">
      {(["all", "unclaimed", "claimed"] as const).map((s) => (
        <Button
          key={s}
          size="sm"
          variant={statusFilter === s ? "default" : "outline"}
          onClick={() => updateParams({ status: s === "all" ? null : s, page: null })}
          data-action={`filter-${s}`}
        >
          {s === "all" ? "All" : s === "unclaimed" ? "Unclaimed" : "Claimed"}
        </Button>
      ))}
    </div>
  );

  return (
    <div className="flex flex-1 overflow-hidden" data-page="parent-invites">
      <div className="flex-1 overflow-auto p-6">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold">Parent Invites</h1>
            <PreviewPaneToggle pinned={pinned} onTogglePin={() => setPinned((p) => !p)} />
          </div>
          <div className="flex items-center gap-2">
            <SchoolFilter />
            {effectiveSchoolId && canManage && (
              <Link
                to={`/schools/${effectiveSchoolId}/parent-invites/import`}
                className={buttonVariants({ size: "sm" })}
                data-action="import-parents"
              >
                <Upload className="h-4 w-4 mr-1" />
                Import
              </Link>
            )}
            {/* 164 -- blast unique registration links to unclaimed seeded parents */}
            {effectiveSchoolId && canManage && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={issuing}
                data-action="issue-registration-links"
                onClick={async () => {
                  if (!window.confirm("Send a unique registration link to every unclaimed parent email at this school?")) return;
                  setIssuing(true);
                  try {
                    const res = await api.issueRegistrationLinks(effectiveSchoolId);
                    window.alert(`Sent ${res.sent} registration link${res.sent === 1 ? "" : "s"}.`);
                  } catch (e) {
                    window.alert(e instanceof api.ApiError ? e.message : "Failed to send links");
                  } finally {
                    setIssuing(false);
                  }
                }}
              >
                <Send className="h-4 w-4 mr-1" />
                {issuing ? "Sending..." : "Send links"}
              </Button>
            )}
            {/* 165 -- yearly active-parent reconciliation */}
            {effectiveSchoolId && canManage && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={reconciling}
                data-action="reconcile-parents"
                onClick={async () => {
                  const raw = window.prompt("Paste the school's current active-parent emails (comma or newline separated):");
                  if (!raw) return;
                  const emails = raw.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
                  if (emails.length === 0) return;
                  setReconciling(true);
                  try {
                    const { result } = await api.reconcileActiveParents(effectiveSchoolId, emails);
                    window.alert(
                      `Reconciliation complete:\n` +
                      `${result.confirmed_active} confirmed active\n` +
                      `${result.flags_cleared} inactive flags cleared\n` +
                      `${result.not_in_list} held accounts not on the list (review).`,
                    );
                  } catch (e) {
                    window.alert(e instanceof api.ApiError ? e.message : "Reconciliation failed");
                  } finally {
                    setReconciling(false);
                  }
                }}
              >
                <Check className="h-4 w-4 mr-1" />
                {reconciling ? "Reconciling..." : "Reconcile"}
              </Button>
            )}
          </div>
        </div>

        <DataTable
          columns={columns}
          fetcher={fetcher}
          fetchKey={`${effectiveSchoolId}:${statusFilter}:${refreshKey}`}
          rowKey={(inv) => inv.id}
          rowProps={(inv) => ({ "data-action": "select-invite", "data-item-id": inv.id })}
          pageSize={PAGE_SIZE}
          searchPlaceholder="Search by name, email, or family code..."
          toolbarExtra={statusFilterControls}
          defaultSort="created_at"
          defaultOrder="desc"
          emptyMessage={
            !effectiveSchoolId
              ? "Select a school to view parent invites."
              : "No parent invites yet. Import a CSV to get started."
          }
          onRowClick={(inv) => setSelected(inv)}
          rowClassName={(inv) => (selected?.id === inv.id ? "bg-muted" : undefined)}
        />
      </div>

      {/* Preview Pane */}
      <PreviewPane
        pinned={pinned}
        onTogglePin={() => setPinned((p) => !p)}
        title={detailInvite ? `${detailInvite.first_name} ${detailInvite.last_name}` : "Parent Invite"}
      >
        {detailInvite && (
          <>
            <PreviewField label="Email">{detailInvite.email}</PreviewField>
            <PreviewField label="Phone">{detailInvite.phone ?? "--"}</PreviewField>
            <PreviewField label="Family Code">
              <code className="text-xs bg-muted px-1 py-0.5 rounded">{detailInvite.family_code}</code>
            </PreviewField>
            <PreviewField label="Status">
              {detailInvite.claimed_at ? (
                <span className="text-green-700">Claimed on {new Date(detailInvite.claimed_at).toLocaleDateString()}</span>
              ) : (
                <span className="text-amber-700">Pending</span>
              )}
            </PreviewField>
            {detailInvite.claimed_user && (
              <PreviewField label="Claimed By">
                {detailInvite.claimed_user.display_name ?? detailInvite.claimed_user.email}
              </PreviewField>
            )}
            <PreviewField label="Imported">
              {new Date(detailInvite.created_at).toLocaleDateString()}
            </PreviewField>

            {matchedPlayers.length > 0 && (
              <PreviewField label={`Matched Players (${matchedPlayers.length})`}>
                <ul className="text-sm space-y-1">
                  {matchedPlayers.map((p) => (
                    <li key={p.id} className="flex items-center gap-1">
                      <span>{p.first_name} {p.surname}</span>
                      {!p.active && <span className="text-xs text-muted-foreground">(inactive)</span>}
                    </li>
                  ))}
                </ul>
              </PreviewField>
            )}

            {canManage && detailInvite._actions.includes("revoke") && (
              <div className="mt-4">
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => handleRevoke(detailInvite.id)}
                  data-action="revoke-detail"
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Revoke Invite
                </Button>
              </div>
            )}
          </>
        )}
      </PreviewPane>
    </div>
  );
}
